"use client"
import { useState, useEffect } from "react"
import { X, Plus, Film, FileText, FileQuestion, WandIcon, Loader } from "lucide-react"

// Content Menu Modal
export const ContentMenuModal = ({ contentManagement, aiFeatures }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Add Content</h2>
          <button
            onClick={() => contentManagement.setShowContentMenu(null)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          <button
            onClick={() => contentManagement.openContentModal("video")}
            className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors"
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
              <Film className="h-6 w-6 text-red-600" />
            </div>
            <div className="text-left">
              <h3 className="font-medium text-gray-900">Add Video</h3>
              <p className="text-sm text-gray-500">Upload a video file or add a YouTube link</p>
            </div>
          </button>
          <button
            onClick={() => contentManagement.openContentModal("pdf")}
            className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-medium text-gray-900">Add PDF</h3>
              <p className="text-sm text-gray-500">Upload a PDF document for your students</p>
            </div>
          </button>
          <button
            onClick={() => {
              contentManagement.openContentModal("quiz")
              aiFeatures.setIsAiQuizMode(false)
            }}
            className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
              <FileQuestion className="h-6 w-6 text-purple-600" />
            </div>
            <div className="text-left">
              <h3 className="font-medium text-gray-900">Add Quiz</h3>
              <p className="text-sm text-gray-500">Create a quiz to test student knowledge</p>
            </div>
          </button>
          <button
            onClick={() => {
              contentManagement.openContentModal("quiz")
              aiFeatures.setIsAiQuizMode(true)
              setTimeout(() => {
                const topicId = contentManagement.showContentMenu
                  ? contentManagement.showContentMenu.replace("add-", "")
                  : contentManagement.currentTopic
                if (topicId) {
                  console.log("Fetching subtopics for topic:", topicId)
                  const subtopics = aiFeatures.fetchSubtopicsForQuiz(topicId)
                  console.log("Available subtopics:", subtopics)
                } else {
                  console.log("No topic ID found in showContentMenu:", contentManagement.showContentMenu)
                }
              }, 100)
            }}
            className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors"
          >
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
              <WandIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="text-left">
              <h3 className="font-medium text-gray-900">Generate AI Quiz</h3>
              <p className="text-sm text-gray-500">Automatically create a quiz based on your content</p>
            </div>
          </button>
        </div>
        <div className="mt-6">
          <button
            onClick={() => contentManagement.setShowContentMenu(null)}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// Content Modal - Fixed to use individual props like the original
export const ContentModal = ({
  isContentModalOpen,
  closeContentModal,
  contentType,
  videoData,
  setVideoData,
  pdfData,
  setPdfData,
  quizData,
  setQuizData,
  handleVideoSubmit,
  handlePdfSubmit,
  handleQuizSubmit,
  handleVideoFileChange,
  handlePdfFileChange,
  handleQuizQuestionChange,
  handleQuizOptionChange,
  addQuizQuestion,
  removeQuizQuestion,
  uploadProgress,
  addingSubtopicId,
  fileInputRef,
  pdfInputRef,
  youtubeInputRef,
  aiFeatures,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {contentType === "video"
              ? "Add Video"
              : contentType === "pdf"
                ? "Add PDF"
                : aiFeatures && aiFeatures.isAiQuizMode
                  ? "Generate AI Quiz"
                  : "Add Quiz"}
          </h2>
          <button
            onClick={closeContentModal}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {contentType === "video" && (
          <form onSubmit={handleVideoSubmit} className="space-y-6">
            <div>
              <label htmlFor="video-title" className="block text-sm font-medium text-gray-700 mb-1">
                Video Title
              </label>
              <input
                id="video-title"
                type="text"
                value={videoData.title}
                onChange={(e) => setVideoData({ ...videoData, title: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter video title"
                required
              />
            </div>
            <div>
              <label htmlFor="video-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                id="video-description"
                value={videoData.description}
                onChange={(e) => setVideoData({ ...videoData, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]"
                placeholder="Enter video description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Video Type</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="video-type"
                    value="file"
                    checked={videoData.videoType === "file"}
                    onChange={() => setVideoData({ ...videoData, videoType: "file" })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Upload Video</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="video-type"
                    value="youtube"
                    checked={videoData.videoType === "youtube"}
                    onChange={() => setVideoData({ ...videoData, videoType: "youtube" })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">YouTube Video</span>
                </label>
              </div>
            </div>
            {videoData.videoType === "file" ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Video</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <Film className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>Upload a video</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept="video/*"
                          ref={fileInputRef}
                          onChange={handleVideoFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">MP4, MOV, AVI up to 500MB</p>
                  </div>
                </div>
                {videoData.previewUrl && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                    <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden bg-black">
                      <video src={videoData.previewUrl} controls className="w-full h-full">
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label htmlFor="youtube-url" className="block text-sm font-medium text-gray-700 mb-1">
                  YouTube URL
                </label>
                <input
                  id="youtube-url"
                  type="text"
                  value={videoData.youtubeUrl}
                  onChange={(e) => setVideoData({ ...videoData, youtubeUrl: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="https://www.youtube.com/watch?v=..."
                  ref={youtubeInputRef}
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=abcdefg)
                </p>
              </div>
            )}
            {uploadProgress > 0 && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <p className="text-sm text-gray-600 mt-1 text-center">
                  {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : "Upload complete!"}
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeContentModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addingSubtopicId !== null}
                className={`px-4 py-2 rounded-md ${
                  addingSubtopicId !== null ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                } text-white transition-colors`}
              >
                {addingSubtopicId !== null ? (
                  <>
                    <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Video"
                )}
              </button>
            </div>
          </form>
        )}
        {contentType === "pdf" && (
          <form onSubmit={handlePdfSubmit} className="space-y-6">
            <div>
              <label htmlFor="pdf-title" className="block text-sm font-medium text-gray-700 mb-1">
                PDF Title
              </label>
              <input
                id="pdf-title"
                type="text"
                value={pdfData.title}
                onChange={(e) => setPdfData({ ...pdfData, title: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter PDF title"
                required
              />
            </div>
            <div>
              <label htmlFor="pdf-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                id="pdf-description"
                value={pdfData.description}
                onChange={(e) => setPdfData({ ...pdfData, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]"
                placeholder="Enter PDF description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Upload PDF</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="pdf-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a PDF</span>
                      <input
                        id="pdf-upload"
                        name="pdf-upload"
                        type="file"
                        className="sr-only"
                        accept="application/pdf"
                        ref={pdfInputRef}
                        onChange={handlePdfFileChange}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF up to 50MB</p>
                </div>
              </div>
              {pdfData.previewUrl && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preview</label>
                  <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
                    <iframe src={pdfData.previewUrl} className="w-full h-full" title="PDF Preview">
                      Your browser does not support embedded PDFs.
                    </iframe>
                  </div>
                </div>
              )}
            </div>
            {uploadProgress > 0 && (
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <p className="text-sm text-gray-600 mt-1 text-center">
                  {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : "Upload complete!"}
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeContentModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addingSubtopicId !== null}
                className={`px-4 py-2 rounded-md ${
                  addingSubtopicId !== null ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                } text-white transition-colors`}
              >
                {addingSubtopicId !== null ? (
                  <>
                    <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add PDF"
                )}
              </button>
            </div>
          </form>
        )}
        {contentType === "quiz" && (!aiFeatures || !aiFeatures.isAiQuizMode) && (
          <form onSubmit={handleQuizSubmit} className="space-y-6">
            <div>
              <label htmlFor="quiz-title" className="block text-sm font-medium text-gray-700 mb-1">
                Quiz Title
              </label>
              <input
                id="quiz-title"
                type="text"
                value={quizData.title}
                onChange={(e) => setQuizData({ ...quizData, title: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter quiz title"
                required
              />
            </div>
            <div>
              <label htmlFor="quiz-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                id="quiz-description"
                value={quizData.description}
                onChange={(e) => setQuizData({ ...quizData, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]"
                placeholder="Enter quiz description"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Questions</label>
                <button
                  type="button"
                  onClick={addQuizQuestion}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Question
                </button>
              </div>
              <div className="space-y-6">
                {quizData.questions.map((question, questionIndex) => (
                  <div key={question.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Question {questionIndex + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeQuizQuestion(question.id)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mb-4">
                      <input
                        type="text"
                        value={question.text}
                        onChange={(e) => handleQuizQuestionChange(question.id, "text", e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter question text"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                      {question.options.map((option, optionIndex) => (
                        <div key={option.id} className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name={`question-${question.id}-correct`}
                            checked={option.isCorrect}
                            onChange={() => handleQuizOptionChange(question.id, option.id, "isCorrect", true)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <input
                            type="text"
                            value={option.text}
                            onChange={(e) => handleQuizOptionChange(question.id, option.id, "text", e.target.value)}
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder={`Option ${optionIndex + 1}`}
                            required
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeContentModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addingSubtopicId !== null}
                className={`px-4 py-2 rounded-md ${
                  addingSubtopicId !== null ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                } text-white transition-colors`}
              >
                {addingSubtopicId !== null ? (
                  <>
                    <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Quiz"
                )}
              </button>
            </div>
          </form>
        )}
        {contentType === "quiz" && aiFeatures && aiFeatures.isAiQuizMode && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              aiFeatures.handleAiQuizSubmit(e).then(() => {
                closeContentModal()
              })
            }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="ai-quiz-title" className="block text-sm font-medium text-gray-700 mb-1">
                Quiz Title
              </label>
              <input
                id="ai-quiz-title"
                type="text"
                value={aiFeatures.aiQuizData.title}
                onChange={(e) => aiFeatures.setAiQuizData({ ...aiFeatures.aiQuizData, title: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter quiz title"
                required
              />
            </div>
            <div>
              <label htmlFor="ai-quiz-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                id="ai-quiz-description"
                value={aiFeatures.aiQuizData.description}
                onChange={(e) => aiFeatures.setAiQuizData({ ...aiFeatures.aiQuizData, description: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]"
                placeholder="Enter quiz description"
              />
            </div>
            <div>
              <label htmlFor="question-count" className="block text-sm font-medium text-gray-700 mb-1">
                Number of Questions (1-20)
              </label>
              <input
                id="question-count"
                type="number"
                min="1"
                max="20"
                value={aiFeatures.aiQuizData.questionCount}
                onChange={(e) => {
                  const value = Number.parseInt(e.target.value)
                  const validValue = Math.min(Math.max(value || 1, 1), 20)
                  aiFeatures.setAiQuizData({ ...aiFeatures.aiQuizData, questionCount: validValue })
                }}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty Level
              </label>
              <select
                id="difficulty"
                value={aiFeatures.aiQuizData.difficulty}
                onChange={(e) => aiFeatures.setAiQuizData({ ...aiFeatures.aiQuizData, difficulty: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Select Content to Include in Quiz</label>
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
                {aiFeatures.subtopicsForQuiz && aiFeatures.subtopicsForQuiz.length > 0 ? (
                  <div className="space-y-2">
                    {aiFeatures.subtopicsForQuiz.map((subtopic) => (
                      <div key={subtopic.id} className="flex items-center p-1 hover:bg-gray-50 rounded-md">
                        <input
                          type="checkbox"
                          id={`subtopic-${subtopic.id}`}
                          checked={aiFeatures.selectedSubtopics.includes(subtopic.id)}
                          onChange={() => aiFeatures.toggleSubtopicSelection(subtopic.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`subtopic-${subtopic.id}`}
                          className="ml-3 block text-sm font-medium text-gray-700 cursor-pointer flex items-center"
                        >
                          {subtopic.type === "video" ? (
                            <Film className="h-3 w-3 text-red-500 mr-1" />
                          ) : subtopic.type === "pdf" ? (
                            <FileText className="h-3 w-3 text-blue-500 mr-1" />
                          ) : null}
                          {subtopic.title}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <div className="mb-2">No content available for quiz generation.</div>
                    <div className="text-sm text-gray-400">
                      This topic needs videos or PDFs before you can generate a quiz.
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={closeContentModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={aiFeatures.generatingQuiz || aiFeatures.selectedSubtopics.length === 0}
                className={`px-4 py-2 rounded-md ${
                  aiFeatures.generatingQuiz || aiFeatures.selectedSubtopics.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white transition-colors`}
              >
                {aiFeatures.generatingQuiz ? (
                  <>
                    <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate Quiz"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// Edit Subtopic Modal
export const EditSubtopicModal = ({ contentManagement }) => {
  const [localData, setLocalData] = useState(null)

  useEffect(() => {
    if (contentManagement.editingSubtopicData) {
      setLocalData(contentManagement.editingSubtopicData)
      console.log("Initializing local data:", contentManagement.editingSubtopicData)
    }
  }, [contentManagement.editingSubtopicData])

  const handleChange = (field, value) => {
    if (!localData) return
    console.log(`Updating ${field} to:`, value)
    const updatedData = { ...localData, [field]: value }
    setLocalData(updatedData)
    if (contentManagement.setEditingSubtopicData) {
      contentManagement.setEditingSubtopicData(updatedData)
    }
  }

  const handleVideoTypeChange = (type) => {
    console.log("Changing video type to:", type)
    handleChange("videoType", type)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (contentManagement.handleUpdateSubtopic) {
      contentManagement.handleUpdateSubtopic(e)
    }
  }

  if (!localData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-6">
          <Loader className="animate-spin h-8 w-8 mx-auto" />
          <p className="text-center mt-2">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Edit Content</h2>
          <button
            onClick={contentManagement.closeEditSubtopicModal}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              id="edit-title"
              type="text"
              value={localData.title || ""}
              onChange={(e) => handleChange("title", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter title"
              required
            />
          </div>
          <div>
            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="edit-description"
              value={localData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]"
              placeholder="Enter description"
            />
          </div>
          {localData.type === "video" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video Type</label>
                <div className="flex space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      id="video-type-file"
                      name="edit-video-type"
                      value="file"
                      checked={localData.videoType === "file"}
                      onChange={() => handleVideoTypeChange("file")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Upload Video</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      id="video-type-youtube"
                      name="edit-video-type"
                      value="youtube"
                      checked={localData.videoType === "youtube"}
                      onChange={() => handleVideoTypeChange("youtube")}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">YouTube Video</span>
                  </label>
                </div>
              </div>
              {localData.videoType === "file" ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {localData.hasFileChanged ? "Replace Video" : "Current Video"}
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <Film className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="edit-file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                        >
                          <span>{localData.newFile ? localData.newFile.name : "Upload a new video"}</span>
                          <input
                            id="edit-file-upload"
                            name="edit-file-upload"
                            type="file"
                            className="sr-only"
                            accept="video/*"
                            ref={contentManagement.editFileInputRef}
                            onChange={contentManagement.handleEditFileChange}
                          />
                        </label>
                        {!localData.newFile && <p className="pl-1">or drag and drop</p>}
                      </div>
                      <p className="text-xs text-gray-500">MP4, MOV, AVI up to 500MB</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label htmlFor="edit-youtube-url" className="block text-sm font-medium text-gray-700 mb-1">
                    YouTube URL
                  </label>
                  <input
                    id="edit-youtube-url"
                    type="text"
                    value={localData.youtubeUrl || ""}
                    onChange={(e) => handleChange("youtubeUrl", e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="https://www.youtube.com/watch?v=..."
                    required={localData.videoType === "youtube"}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=abcdefg)
                  </p>
                </div>
              )}
            </>
          )}
          {localData.type === "pdf" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {localData.hasFileChanged ? "Replace PDF" : "Current PDF"}
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="edit-pdf-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>{localData.newFile ? localData.newFile.name : "Upload a new PDF"}</span>
                      <input
                        id="edit-pdf-upload"
                        name="edit-pdf-upload"
                        type="file"
                        className="sr-only"
                        accept="application/pdf"
                        ref={contentManagement.editFileInputRef}
                        onChange={contentManagement.handleEditFileChange}
                      />
                    </label>
                    {!localData.newFile && <p className="pl-1">or drag and drop</p>}
                  </div>
                  <p className="text-xs text-gray-500">PDF up to 50MB</p>
                </div>
              </div>
            </div>
          )}
          {localData.type === "quiz" && localData.quizData && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Questions</label>
                <button
                  type="button"
                  onClick={contentManagement.addEditQuizQuestion}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Question
                </button>
              </div>
              <div className="space-y-6">
                {localData.quizData.map((question, questionIndex) => (
                  <div key={question.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium text-gray-700">Question {questionIndex + 1}</h4>
                      <button
                        type="button"
                        onClick={() => contentManagement.removeEditQuizQuestion(question.id)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mb-4">
                      <input
                        type="text"
                        value={question.text}
                        onChange={(e) =>
                          contentManagement.handleEditQuizQuestionChange(question.id, "text", e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter question text"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                      {question.options.map((option, optionIndex) => (
                        <div key={option.id} className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name={`edit-question-${question.id}-correct`}
                            checked={option.isCorrect}
                            onChange={() =>
                              contentManagement.handleEditQuizOptionChange(question.id, option.id, "isCorrect", true)
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <input
                            type="text"
                            value={option.text}
                            onChange={(e) =>
                              contentManagement.handleEditQuizOptionChange(
                                question.id,
                                option.id,
                                "text",
                                e.target.value,
                              )
                            }
                            className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder={`Option ${optionIndex + 1}`}
                            required
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {contentManagement.uploadProgress > 0 && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${contentManagement.uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-1 text-center">
                {contentManagement.uploadProgress < 100
                  ? `Uploading... ${contentManagement.uploadProgress}%`
                  : "Upload complete!"}
              </p>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={contentManagement.closeEditSubtopicModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={contentManagement.isUpdatingSubtopic}
              className={`px-4 py-2 rounded-md ${
                contentManagement.isUpdatingSubtopic
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white transition-colors`}
            >
              {contentManagement.isUpdatingSubtopic ? (
                <>
                  <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Content"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Course Outline Confirmation Modal
export const CourseOutlineConfirmationModal = ({ aiFeatures }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Generated Course Outline</h2>
          <button
            onClick={() => aiFeatures.setShowOutlineConfirmation(false)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            We've generated a course outline based on your course title and description. Review it below and click "Add
            to Course" to use this structure.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
            <h3 className="font-bold text-lg mb-2">{aiFeatures.generatedOutline.title}</h3>
            <p className="text-gray-600 mb-4">{aiFeatures.generatedOutline.description}</p>
            <div className="space-y-4 mt-4">
              {aiFeatures.generatedOutline.topics.map((topic, topicIndex) => (
                <div key={topicIndex} className="border-l-2 border-blue-500 pl-4">
                  <h4 className="font-medium text-blue-700">
                    Chapter {topicIndex + 1}: {topic.title}
                  </h4>
                  <ul className="mt-2 space-y-2">
                    {topic.subtopics.map((subtopic, subtopicIndex) => (
                      <li key={subtopicIndex} className="flex items-start">
                        <span className="text-gray-400 mr-2">•</span>
                        <span className="text-gray-700">{subtopic.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => aiFeatures.setShowOutlineConfirmation(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={aiFeatures.addGeneratedOutlineToCourse}
            disabled={aiFeatures.isSaving}
            className={`px-4 py-2 rounded-md ${
              aiFeatures.isSaving ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            } text-white transition-colors`}
          >
            {aiFeatures.isSaving ? (
              <>
                <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add to Course"
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
